import { useEffect } from 'react';

import { useLocation, useNavigate } from 'react-router';

import {
  IconEdit,
  IconEye,
  IconEyeOff,
  IconGripVertical,
  IconTrash,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Tag } from '@cfa/react-components';

import { IntlCategoryEntity } from '@/services/content-api-types';
import { LanguageObject, LocationState } from '@/types/types';

import PopoverMenuButton from 'components/PopoverMenuButton/PopoverMenuButton';
import PopoverMenuButtonItem from 'components/PopoverMenuButton/PopoverMenuButtonItem';
import Constants from 'constants/index';
import { getNameFromLanguage } from 'utils/language';

import { useIsAdmin } from '../hooks';

interface CardCategoryProps {
  category: IntlCategoryEntity;
  gripClassName: string;
  onCategoryAdminPopupMenu?: (category: IntlCategoryEntity) => void;
  onVisibilityPress?: (category: IntlCategoryEntity) => void;
  onDelete?: (category: IntlCategoryEntity) => void;
  onEdit?: (category: IntlCategoryEntity) => void;
  onClickCategory?: (category: IntlCategoryEntity) => void;
  onSixDotMenuIconClick?: () => void;
}

const CardCategory: React.FC<CardCategoryProps> = ({
  category,
  gripClassName,
  onCategoryAdminPopupMenu,
  onVisibilityPress,
  onDelete,
  onEdit,
  onClickCategory,
  onSixDotMenuIconClick,
}) => {
  const { name, icon, id } = category;
  const navigate = useNavigate();
  const { state } = useLocation() as { state?: LocationState };
  const { t } = useTranslation();
  const userIsAdmin = useIsAdmin();

  useEffect(() => {
    if (state?.fromTrainTogetherMode) {
      navigate(
        { pathname: `/` },
        { replace: true, state: { fromTrainTogetherMode: false } },
      );
    }
  }, [navigate, state]);

  return (
    <div className="pathway-card text-left" data-testid={`${id}-card`}>
      <div className="m-0 flex w-full items-center">
        {userIsAdmin && (
          <div className={gripClassName}>
            <IconGripVertical
              onClick={onSixDotMenuIconClick}
              className="text-grayscale-gray3 mr-2 cursor-move"
            />
          </div>
        )}
        <button
          type="button"
          onClick={() => {
            onClickCategory?.(category);
            onEdit?.(category);
          }}
          className="flex w-full overflow-hidden text-left text-ellipsis"
        >
          <img
            alt=""
            src={`${Constants.PATHWAY_CDN_IMG.CATEGORIES}${icon}.svg`}
            className="mr-2.5 size-[40px]"
          />
          <div className="text-grayscale-gray6 dark:text-contrast my-auto line-clamp-2 grow overflow-hidden">
            {getNameFromLanguage(name as LanguageObject)}
          </div>
        </button>
        {userIsAdmin && (
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => onVisibilityPress?.(category)}
              className="cursor-pointer"
            >
              <Tag
                data-testid="CategoryCard"
                disabled={category.enabled ? false : true}
                label={
                  category.enabled
                    ? t('Admin.categoryIsVisible')
                    : t('Admin.categoryHidden')
                }
                leadingElement={
                  category.enabled ? (
                    <IconEye width={10} />
                  ) : (
                    <IconEyeOff width={10} />
                  )
                }
                variant="filled"
                className="ml-4! flex-row-reverse! items-center! gap-1! px-1.5! py-1! [&_.tag-label]:font-normal! [&_.tag-leading-element]:mr-0! [&_.tag-leading-element]:ml-1 [&_.tag-leading-element]:size-auto!"
              />
            </button>

            <PopoverMenuButton
              data-testid="CategoryCardMenu"
              handleClick={() => onCategoryAdminPopupMenu?.(category)}
            >
              <PopoverMenuButtonItem
                dataTestId="CategoryCardEdit"
                icon={<IconEdit />}
                onClick={() => onEdit?.(category)}
                text={t('Button.edit')}
              />
              <PopoverMenuButtonItem
                dataTestId="CategoryCardDelete"
                icon={<IconTrash />}
                isDestructive
                onClick={() => onDelete?.(category)}
                text={t('Button.delete')}
              />
            </PopoverMenuButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default CardCategory;
